import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Download,
  Edit,
  Plus,
  UserPlus,
  Users,
  Search,
  CheckSquare,
  Trash2,
  Loader2,
  AlertCircle,
  Mail,
  Calendar,
  Hash,
  Copy,
  X,
  FileSignature,
  ShieldCheck,
  ArrowLeft,
  Check,
  Eye,
  EyeOff,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';
import PdfViewer from '../components/PdfViewer';
import { documentApi, signerApi, organizationApi } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { useContactStore, SavedContact } from '../stores/contactStore';
import { toast, confirmDialog } from '../stores/feedbackStore';
import { formatDateTime, formatRelativeTime, formatFileSize, maskEmail } from '@pdfsign/shared/utils';
import { DOCUMENT_TYPE_LABELS } from '@pdfsign/shared/types';
import { Document, DocumentStatus, DocumentSigner, SignerStatus, SignerRole } from '@pdfsign/shared/types';

const statusConfig: Record<DocumentStatus, { label: string; color: string; icon: React.ReactNode }> = {
  draft: { label: 'Bản nháp', color: 'badge-gray', icon: <FileText className="w-3.5 h-3.5" /> },
  pending_sign: { label: 'Chờ ký', color: 'badge-warning', icon: <Clock className="w-3.5 h-3.5" /> },
  in_progress: { label: 'Đang ký', color: 'badge-primary', icon: <Clock className="w-3.5 h-3.5" /> },
  completed: { label: 'Hoàn tất', color: 'badge-success', icon: <CheckCircle className="w-3.5 h-3.5" /> },
  rejected: { label: 'Từ chối', color: 'badge-danger', icon: <XCircle className="w-3.5 h-3.5" /> },
  expired: { label: 'Hết hạn', color: 'badge-gray', icon: <Clock className="w-3.5 h-3.5" /> },
  cancelled: { label: 'Đã hủy', color: 'badge-gray', icon: <XCircle className="w-3.5 h-3.5" /> },
};

const signerStatusConfig: Record<SignerStatus, { label: string; color: string }> = {
  pending: { label: 'Chờ mời', color: 'badge-gray' },
  invited: { label: 'Đã gửi lời mời', color: 'badge-primary' },
  viewed: { label: 'Đã xem tài liệu', color: 'badge-primary' },
  signed: { label: 'Đã ký xác nhận', color: 'badge-success' },
  declined: { label: 'Đã từ chối', color: 'badge-danger' },
  expired: { label: 'Đã hết hạn', color: 'badge-gray' },
};

const roleLabels: Record<SignerRole, string> = {
  signer: 'Người ký',
  approver: 'Người duyệt',
  cc: 'Người nhận bản sao',
  witness: 'Người chứng thực',
};

const addSignerSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
  full_name: z.string().min(1, 'Họ tên là bắt buộc').max(100),
  role: z.enum(['signer', 'approver', 'cc', 'witness']).default('signer'),
  order: z.number().int().min(0).optional(),
  signature_type: z.enum(['draw', 'image', 'text', 'digital']).optional(),
});

type AddSignerForm = z.infer<typeof addSignerSchema>;

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<Document | null>(null);
  const [signers, setSigners] = useState<DocumentSigner[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddSigner, setShowAddSigner] = useState(false);
  const [copiedHash, setCopiedHash] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(true);
  const [previewPage, setPreviewPage] = useState(1);

  const { user } = useAuthStore();
  const isOwner = Boolean(document?.created_by && user?.id && document.created_by === user.id);
  const isAdmin = user?.role === 'admin';

  const [editingSigningDate, setEditingSigningDate] = useState(false);
  const [tempSigningDate, setTempSigningDate] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddSignerForm>({
    resolver: zodResolver(addSignerSchema),
    defaultValues: {
      role: 'signer',
      signature_type: 'draw',
    },
  });

  const fetchDocument = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await documentApi.get(id);
      const docData = response.data.data;
      setDocument(docData);
      setSigners(docData.signers || []);
      setTempSigningDate(docData.settings?.signing_date || '');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể tải tài liệu');
      if (err.response?.status === 404) {
        navigate('/documents');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocument();
  }, [id]);

  const { contacts, addContact, deleteContact, mergeOrganizationMembers } = useContactStore();
  const [signerModalTab, setSignerModalTab] = useState<'contacts' | 'manual'>('contacts');
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);
  const [contactRoles, setContactRoles] = useState<Record<string, SignerRole>>({});
  const [contactSearch, setContactSearch] = useState('');
  const [saveToContacts, setSaveToContacts] = useState(true);

  // Sync with organization members if available
  useEffect(() => {
    if (!user?.organization_id) return;
    organizationApi.listMembers(user.organization_id, { limit: 100 })
      .then(res => {
        const members = res.data?.data || [];
        if (Array.isArray(members) && members.length > 0) {
          mergeOrganizationMembers(members);
        }
      })
      .catch(() => {});
  }, [user?.organization_id, mergeOrganizationMembers]);

  const handleAddSelectedSigners = async () => {
    if (!document || selectedContactIds.length === 0) return;

    setActionLoading('send');
    setError(null);
    try {
      const signersToAdd = selectedContactIds.map((cId, idx) => {
        const contact = contacts.find(c => c.id === cId)!;
        return {
          email: contact.email,
          full_name: contact.full_name,
          role: contactRoles[cId] || contact.role || 'signer',
          order: idx + 1,
          signature_type: contact.signature_type || 'draw',
        };
      });

      await documentApi.send(document.id, {
        signers: signersToAdd,
      });

      setShowAddSigner(false);
      setSelectedContactIds([]);
      setSuccess(`Đã thêm ${signersToAdd.length} người ký vào luồng thành công`);
      fetchDocument();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể thêm người ký vào luồng');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSend = async (data: AddSignerForm) => {
    if (!document) return;
    
    setActionLoading('send');
    setError(null);
    try {
      await documentApi.send(document.id, {
        signers: [{
          email: data.email,
          full_name: data.full_name,
          role: data.role,
          order: data.order,
          signature_type: data.signature_type,
        }],
      });

      if (saveToContacts) {
        addContact({
          full_name: data.full_name,
          email: data.email,
          role: data.role,
          signature_type: data.signature_type || 'draw',
        });
      }

      setShowAddSigner(false);
      reset();
      setSuccess('Đã thêm người ký vào luồng thành công');
      fetchDocument();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể gửi mời ký');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemind = async (signerId: string) => {
    setActionLoading(`remind-${signerId}`);
    try {
      await signerApi.remind(signerId);
      setSuccess('Đã gửi nhắc nhở');
      fetchDocument();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Không thể gửi nhắc nhở');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetSigner = async (signerId: string, signerName: string) => {
    if (!document) return;
    const ok = await confirmDialog({
      title: 'Hủy chữ ký và yêu cầu ký lại',
      message: `Bạn có chắc chắn muốn hủy chữ ký của "${signerName}" và yêu cầu họ ký lại? Chữ ký của các người ký khác (nếu có) sẽ được giữ nguyên 100%.`,
      confirmText: 'Đồng ý hủy để ký lại',
      cancelText: 'Đóng',
      type: 'warning',
    });
    if (!ok) return;

    setActionLoading(`reset-${signerId}`);
    try {
      await signerApi.reset(signerId);
      toast.success(`Đã hủy chữ ký của ${signerName} và mở lại trạng thái chờ ký.`);
      fetchDocument();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Không thể hủy chữ ký');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateSigningDate = async () => {
    if (!document) return;
    setActionLoading('update-signing-date');
    try {
      await documentApi.update(document.id, {
        signing_date: tempSigningDate || null,
      });
      toast.success(tempSigningDate ? `Đã cập nhật ngày ấn định ký: ${tempSigningDate}` : 'Đã bỏ ấn định ngày (dùng thời gian thực)');
      setEditingSigningDate(false);
      fetchDocument();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Không thể cập nhật ngày ấn định');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async () => {
    if (!document) return;
    const ok = await confirmDialog({
      title: 'Hủy luồng ký tài liệu',
      message: 'Bạn có chắc chắn muốn hủy tài liệu này? Mọi người ký sẽ không thể tiếp tục ký và quy trình sẽ bị dừng.',
      confirmText: 'Hủy tài liệu',
      cancelText: 'Đóng',
      type: 'danger',
    });
    if (!ok) return;
    
    setActionLoading('cancel');
    try {
      await documentApi.cancel(document.id);
      toast.success('Đã hủy tài liệu thành công');
      fetchDocument();
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Không thể hủy tài liệu');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async () => {
    if (!document) return;
    try {
      const response = await documentApi.download(document.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = window.document.createElement('a');
      link.href = url;
      link.setAttribute('download', document.file_name);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Đang tải xuống tệp: ${document.file_name}`);
    } catch (err) {
      toast.error('Không thể tải xuống tài liệu');
    }
  };

  const copySignLink = (signerId: string) => {
    const url = `${window.location.origin}/sign/${signerId}`;
    navigator.clipboard.writeText(url);
    toast.success('Đã sao chép liên kết ký trực tiếp');
  };

  const copyFileHash = () => {
    if (!document?.file_hash) return;
    navigator.clipboard.writeText(document.file_hash);
    setCopiedHash(true);
    setTimeout(() => setCopiedHash(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400 text-xs">
        <Loader2 className="w-6 h-6 text-sky-500 animate-spin mr-2" />
        <span>Đang tải thông tin hồ sơ...</span>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="card p-12 text-center max-w-lg mx-auto mt-8">
        <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-1">Không tìm thấy tài liệu</h3>
        <p className="text-xs text-slate-400 mb-4 font-normal">Hồ sơ có thể đã bị xóa hoặc bạn không có quyền truy cập.</p>
        <Link to="/documents" className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1.5 font-medium">
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Về danh sách tài liệu</span>
        </Link>
      </div>
    );
  }

  const config = statusConfig[document.status] || statusConfig.draft;
  const canEdit = ['draft', 'pending_sign'].includes(document.status);
  const canSend = ['draft', 'pending_sign'].includes(document.status) && signers.length === 0;
  const canAddSigner = ['draft', 'pending_sign', 'in_progress'].includes(document.status);
  const canDownload = Boolean(document.file_name);
  const canCancel = ['draft', 'pending_sign', 'in_progress'].includes(document.status);

  // Check if current user is a pending signer on this document
  const pendingMySigner = user
    ? signers.find(
        (s) =>
          s.email.toLowerCase() === user.email.toLowerCase() &&
          ['invited', 'pending', 'viewed'].includes(s.status)
      )
    : null;

  return (
    <div className="space-y-4 animate-fade-in w-full">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-3 border-b border-slate-200/80 dark:border-white/10">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-sky-50 dark:bg-sky-500/10 border border-sky-200/60 dark:border-sky-500/20 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
            <FileSignature className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base sm:text-lg font-medium tracking-tight text-slate-900 dark:text-white truncate">
                {document.title}
              </h1>
              <span className={`badge ${config.color} text-[10px] font-normal`}>
                {config.icon}
                <span>{config.label}</span>
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-400 font-mono mt-0.5 font-normal">
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3 text-slate-500" />
                {document.id.slice(0, 8)}
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3 text-slate-500" />
                {formatRelativeTime(document.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Action buttons toolbar */}
        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          {pendingMySigner && (
            <Link
              to={`/sign/${pendingMySigner.id}`}
              className="btn-primary text-xs h-8 px-3.5 font-medium inline-flex items-center gap-1.5 shadow-md bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white"
            >
              <FileSignature className="w-3.5 h-3.5" />
              <span>Ký tài liệu ngay</span>
            </Link>
          )}

          <Link
            to="/documents"
            className="btn-outline text-xs h-8 px-2.5 font-normal inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span>Quay lại</span>
          </Link>

          {document.file_name && (
            <button
              onClick={() => setShowPdfPreview(!showPdfPreview)}
              className={`text-xs h-8 px-3 font-normal inline-flex items-center gap-1.5 rounded-lg border transition-all ${
                showPdfPreview
                  ? 'bg-sky-500/10 border-sky-500/30 text-sky-400'
                  : 'btn-outline'
              }`}
            >
              {showPdfPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              <span>{showPdfPreview ? 'Ẩn xem trước' : 'Xem trước PDF'}</span>
            </button>
          )}

          {canDownload && (
            <button
              onClick={handleDownload}
              className="btn-outline text-xs h-8 px-3 font-normal inline-flex items-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Tải xuống</span>
            </button>
          )}

          {canSend && (
            <button
              onClick={() => setShowAddSigner(true)}
              className="btn-primary text-xs h-8 px-3 font-medium inline-flex items-center gap-1.5"
            >
              <Send className="w-3.5 h-3.5" />
              <span>Gửi ký</span>
            </button>
          )}

          {canAddSigner && signers.length > 0 && !canSend && (
            <button
              onClick={() => setShowAddSigner(true)}
              className="btn-outline text-xs h-8 px-3 font-normal inline-flex items-center gap-1.5"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Thêm người ký</span>
            </button>
          )}

          {canCancel && (
            <button
              onClick={handleCancel}
              className="btn-danger text-xs h-8 px-2.5 font-normal inline-flex items-center gap-1.5"
            >
              <XCircle className="w-3.5 h-3.5" />
              <span>Hủy</span>
            </button>
          )}

          {canEdit && (
            <Link
              to={`/documents/${document.id}/edit`}
              className="btn-outline text-xs h-8 px-2.5 font-normal inline-flex items-center gap-1.5"
            >
              <Edit className="w-3.5 h-3.5" />
              <span>Chỉnh sửa</span>
            </Link>
          )}
        </div>
      </div>

      {/* Notifications banner */}
      {error && (
        <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center gap-2.5 text-rose-600 dark:text-rose-400 text-xs font-normal">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {success && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-2.5 text-emerald-600 dark:text-emerald-400 text-xs font-normal">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <p>{success}</p>
        </div>
      )}

      {/* Signer Call To Action Banner if logged-in user needs to sign */}
      {pendingMySigner && (
        <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-sky-500/15 via-indigo-500/15 to-emerald-500/15 border-2 border-sky-500/40 dark:border-sky-400/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-sky-500/20 text-sky-600 dark:text-sky-400 flex items-center justify-center flex-shrink-0">
              <FileSignature className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-slate-900 dark:text-white text-sm sm:text-base">
                Bạn có 1 lượt ký cần thực hiện trên tài liệu này!
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-normal">
                Tài khoản <strong className="text-sky-600 dark:text-sky-400 font-mono">{user?.email}</strong> được chỉ định ký với vai trò <strong>{roleLabels[pendingMySigner.role] || 'Người ký'}</strong>.
              </p>
            </div>
          </div>
          <Link
            to={`/sign/${pendingMySigner.id}`}
            className="btn-primary whitespace-nowrap px-4 py-2 shadow-md flex items-center gap-2 text-xs sm:text-sm font-semibold hover:shadow-sky-500/25 bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white flex-shrink-0"
          >
            <FileSignature className="w-4 h-4" />
            <span>Ký tài liệu ngay</span>
          </Link>
        </div>
      )}

      {/* 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left Column (8 cols): PDF Viewer (Primary focus) & Signers List */}
        <div className="lg:col-span-8 space-y-4">
          {/* PDF Viewer Card */}
          {showPdfPreview && document.file_name ? (
            <div className="card p-4 sm:p-5">
              <div className="flex items-center justify-between pb-3 mb-3 border-b border-slate-100 dark:border-white/10 flex-wrap gap-2">
                <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5 text-sky-500" />
                  <span>Bản xem trước văn bản ({document.file_name})</span>
                </h2>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-xs text-slate-400 font-mono">
                    <button
                      onClick={() => setPreviewPage(p => Math.max(1, p - 1))}
                      disabled={previewPage <= 1}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors"
                      title="Trang trước"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <span>Trang {previewPage} / {document.pages_count || 1}</span>
                    <button
                      onClick={() => setPreviewPage(p => Math.min(document.pages_count || 1, p + 1))}
                      disabled={previewPage >= (document.pages_count || 1)}
                      className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors"
                      title="Trang sau"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {canDownload && (
                    <button
                      onClick={handleDownload}
                      className="btn-outline text-xs h-7 px-2.5 font-normal inline-flex items-center gap-1"
                      title="Tải tệp PDF về máy tính"
                    >
                      <Download className="w-3 h-3" />
                      <span>Tải về</span>
                    </button>
                  )}
                </div>
              </div>

              <PdfViewer documentId={document.id} page={previewPage} />
            </div>
          ) : (
            <div className="card p-4 flex items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-900/30 border-dashed">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-sky-500 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate max-w-sm">
                    {document.file_name || 'Chưa tải lên tệp PDF'}
                  </p>
                  <p className="text-[11px] text-slate-400 font-normal">Bản xem trước PDF đang thu gọn</p>
                </div>
              </div>
              {document.file_name && (
                <button
                  onClick={() => setShowPdfPreview(true)}
                  className="btn-primary text-xs h-7 px-2.5 font-normal flex items-center gap-1.5"
                >
                  <Eye className="w-3 h-3" />
                  <span>Mở xem trước</span>
                </button>
              )}
            </div>
          )}

          {/* Description Card */}
          {document.description && (
            <div className="card p-4 sm:p-5">
              <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2">
                Nội dung mô tả
              </h2>
              <p className="text-xs sm:text-sm text-slate-700 dark:text-slate-300 font-normal leading-relaxed whitespace-pre-wrap">
                {document.description}
              </p>
            </div>
          )}

          {/* Signers List Card */}
          <div className="card overflow-hidden">
            <div className="py-2.5 px-4 border-b border-slate-100 dark:border-white/10 flex items-center justify-between bg-slate-50/50 dark:bg-white/[0.02]">
              <h2 className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                Danh sách người ký ({signers.length})
              </h2>
              {canAddSigner && (
                <button
                  onClick={() => setShowAddSigner(true)}
                  className="btn-primary text-xs py-1 px-2.5 inline-flex items-center gap-1 font-medium h-7"
                >
                  <Plus className="w-3 h-3" />
                  <span>Thêm</span>
                </button>
              )}
            </div>

            <div className="divide-y divide-slate-100 dark:divide-white/5">
              {signers.length === 0 ? (
                <div className="p-8 text-center text-slate-400 text-xs font-normal">
                  Chưa có người ký nào trong luồng. Bấm nút "Thêm" để gán người ký văn bản.
                </div>
              ) : (
                signers.map((signer) => {
                  const sConfig = signerStatusConfig[signer.status] || signerStatusConfig.pending;
                  const isCurrentTurn =
                    document.workflow_type === 'sequential' &&
                    signer.order_index === document.current_step &&
                    ['invited', 'viewed', 'pending'].includes(signer.status);

                  return (
                    <div
                      key={signer.id}
                      className="py-2.5 px-3.5 sm:px-4 hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors flex items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 rounded-lg bg-sky-500/10 text-sky-600 dark:text-sky-400 flex items-center justify-center font-medium text-xs flex-shrink-0 border border-sky-500/20">
                          {signer.full_name.charAt(0).toUpperCase()}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                              {signer.full_name}
                            </p>
                            <span className={`badge ${sConfig.color} text-[10px] font-normal`}>
                              {sConfig.label}
                            </span>
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-normal border border-slate-200 dark:border-white/10">
                              {roleLabels[signer.role]}
                            </span>
                            {isCurrentTurn && (
                              <span className="badge bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 text-[10px] font-normal animate-pulse">
                                Đến lượt ký
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 truncate font-mono mt-0.5 font-normal">
                            {maskEmail(signer.email)}
                            {signer.signed_at && (
                              <span className="ml-2 text-emerald-600 dark:text-emerald-400">
                                · Đã ký lúc {formatDateTime(signer.signed_at)}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {signer.id === pendingMySigner?.id && (
                          <Link
                            to={`/sign/${signer.id}`}
                            className="btn-primary text-xs py-1 px-2.5 flex items-center gap-1 shadow-sm bg-gradient-to-r from-sky-500 to-indigo-600 text-white font-medium"
                          >
                            <FileSignature className="w-3 h-3" />
                            <span>Ký ngay</span>
                          </Link>
                        )}
                        {['invited', 'viewed', 'pending'].includes(signer.status) && (
                          <>
                            <a
                              href={`/sign/${signer.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                              title="Mở trang ký trực tiếp"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <button
                              onClick={() => handleRemind(signer.id)}
                              disabled={actionLoading === `remind-${signer.id}`}
                              className="p-1.5 text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                              title="Gửi email nhắc nhở"
                            >
                              <Mail className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => copySignLink(signer.id)}
                              className="p-1.5 text-slate-400 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                              title="Sao chép liên kết ký trực tiếp"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        {signer.status === 'signed' && (
                          <div className="flex items-center gap-1">
                            <span className="p-1 text-emerald-500 flex items-center gap-1 text-xs" title="Đã ký">
                              <CheckCircle className="w-4 h-4" />
                            </span>
                            {(isOwner || isAdmin) && !['completed', 'cancelled', 'rejected', 'expired'].includes(document.status) && (
                              <button
                                onClick={() => handleResetSigner(signer.id, signer.full_name)}
                                disabled={actionLoading === `reset-${signer.id}`}
                                className="p-1.5 text-slate-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                title="Hủy chữ ký và yêu cầu ký lại"
                              >
                                {actionLoading === `reset-${signer.id}` ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <RotateCcw className="w-3.5 h-3.5" />
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right Column (4 cols): Quick Operations & Cryptographic Hash */}
        <div className="lg:col-span-4 space-y-4">
          {/* Quick Actions Card */}
          <div className="card p-4 sm:p-5">
            <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-3">
              Thao tác hồ sơ
            </h3>
            <div className="space-y-2">
              {pendingMySigner && (
                <Link
                  to={`/sign/${pendingMySigner.id}`}
                  className="btn-primary w-full justify-center text-xs h-8 px-3 font-medium gap-2 shadow-sm bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700 text-white flex items-center mb-1"
                >
                  <FileSignature className="w-3.5 h-3.5" />
                  <span>Ký tài liệu ngay</span>
                </Link>
              )}
              {document.file_name && (
                <button
                  onClick={() => setShowPdfPreview(!showPdfPreview)}
                  className="btn-outline w-full justify-start text-xs h-8 px-3 font-normal gap-2"
                >
                  <Eye className="w-3.5 h-3.5 text-sky-500" />
                  <span>{showPdfPreview ? 'Thu gọn bản xem trước' : 'Xem trước tài liệu PDF'}</span>
                </button>
              )}
              {canDownload && (
                <button
                  onClick={handleDownload}
                  className="btn-outline w-full justify-start text-xs h-8 px-3 font-normal gap-2"
                >
                  <Download className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Tải xuống tệp PDF</span>
                </button>
              )}
              {canSend && (
                <button
                  onClick={() => setShowAddSigner(true)}
                  className="btn-primary w-full justify-start text-xs h-8 px-3 font-medium gap-2"
                >
                  <Send className="w-3.5 h-3.5" />
                  <span>Gửi luồng ký lần đầu</span>
                </button>
              )}
              {canAddSigner && signers.length > 0 && !canSend && (
                <button
                  onClick={() => setShowAddSigner(true)}
                  className="btn-outline w-full justify-start text-xs h-8 px-3 font-normal gap-2"
                >
                  <UserPlus className="w-3.5 h-3.5 text-sky-500" />
                  <span>Thêm người ký vào luồng</span>
                </button>
              )}
            </div>
          </div>

          {/* Metadata Card (Thông tin hồ sơ tài liệu) */}
          <div className="card p-4 sm:p-5">
            <h3 className="text-xs font-semibold text-slate-700 dark:text-slate-200 mb-2 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-sky-500" />
              <span>Thông tin hồ sơ tài liệu</span>
            </h3>

            <dl className="divide-y divide-slate-100 dark:divide-white/5 text-xs">
              <div className="py-2 flex items-center justify-between gap-2">
                <dt className="text-slate-400 font-normal">Loại tài liệu</dt>
                <dd className="font-medium text-slate-800 dark:text-slate-200 text-right">
                  {DOCUMENT_TYPE_LABELS[document.type as keyof typeof DOCUMENT_TYPE_LABELS] || document.type}
                </dd>
              </div>

              <div className="py-2 flex items-center justify-between gap-2">
                <dt className="text-slate-400 font-normal">Quy trình ký</dt>
                <dd className="font-medium text-slate-800 dark:text-slate-200 text-right">
                  {document.workflow_type === 'sequential' ? 'Ký lần lượt' : 'Ký song song'}
                </dd>
              </div>

              <div className="py-2 flex items-center justify-between gap-2">
                <dt className="text-slate-400 font-normal">Số trang</dt>
                <dd className="font-medium text-slate-800 dark:text-slate-200 font-mono text-right">
                  {document.pages_count || 1} trang
                </dd>
              </div>

              <div className="py-2 flex items-center justify-between gap-2">
                <dt className="text-slate-400 font-normal">Tiến độ ký</dt>
                <dd className="font-medium text-slate-800 dark:text-slate-200 font-mono text-right">
                  {document.current_step}/{document.total_steps} chữ ký
                </dd>
              </div>

              <div className="py-2 flex items-center justify-between gap-2">
                <dt className="text-slate-400 font-normal">Người khởi tạo</dt>
                <dd className="font-medium text-slate-800 dark:text-slate-200 text-right truncate max-w-[170px]" title={document.creator_email}>
                  {document.creator_name || 'Quản trị viên'}
                  {document.creator_email && (
                    <span className="text-slate-400 font-normal text-[10px] block truncate">
                      {document.creator_email}
                    </span>
                  )}
                </dd>
              </div>

              <div className="py-2 flex items-center justify-between gap-2">
                <dt className="text-slate-400 font-normal">Tệp đính kèm</dt>
                <dd className="font-medium text-slate-800 dark:text-slate-200 text-right truncate max-w-[170px]" title={document.file_name}>
                  {document.file_name ? (
                    <span className="text-sky-500 font-medium truncate inline-block max-w-full">
                      {document.file_name}
                    </span>
                  ) : (
                    <span>Chưa tải lên</span>
                  )}
                  {document.file_size > 0 && (
                    <span className="text-[10px] text-slate-400 font-mono block">
                      {formatFileSize(document.file_size)}
                    </span>
                  )}
                </dd>
              </div>

              <div className="py-2 border-b border-slate-100 dark:border-white/5">
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-slate-400 font-normal">Ngày ấn định ký</dt>
                  <dd className="font-medium text-amber-600 dark:text-amber-400 font-mono text-right text-[11px] flex items-center gap-1">
                    {document.settings?.signing_date ? (
                      <>
                        <Calendar className="w-3 h-3" />
                        <span>{document.settings.signing_date} (Hồi cứu)</span>
                      </>
                    ) : (
                      <span className="text-slate-400 font-sans">Thời gian thực</span>
                    )}
                    {(isOwner || isAdmin) && !['completed', 'cancelled', 'rejected', 'expired'].includes(document.status) && (
                      <button
                        onClick={() => {
                          setTempSigningDate(document.settings?.signing_date || '');
                          setEditingSigningDate(!editingSigningDate);
                        }}
                        className="ml-1 p-1 text-slate-400 hover:text-sky-500 rounded transition-colors"
                        title="Thay đổi ngày ấn định ký"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                    )}
                  </dd>
                </div>
                {editingSigningDate && (
                  <div className="mt-2 pt-2 border-t border-slate-100 dark:border-white/5 flex items-center gap-1.5">
                    <input
                      type="date"
                      value={tempSigningDate}
                      onChange={(e) => setTempSigningDate(e.target.value)}
                      className="input text-xs h-7 py-0 px-2 flex-1"
                    />
                    <button
                      onClick={handleUpdateSigningDate}
                      disabled={actionLoading === 'update-signing-date'}
                      className="btn-primary text-xs h-7 px-2"
                      title="Lưu"
                    >
                      {actionLoading === 'update-signing-date' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => setEditingSigningDate(false)}
                      className="btn-ghost text-xs h-7 px-2"
                      title="Hủy"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>

              {document.expires_at && (
                <div className="py-2 flex items-center justify-between gap-2">
                  <dt className="text-slate-400 font-normal">Hết hạn vào</dt>
                  <dd className="font-medium text-slate-800 dark:text-slate-200 font-mono text-right text-[11px]">
                    {formatDateTime(document.expires_at)}
                  </dd>
                </div>
              )}

              {document.completed_at && (
                <div className="py-2 flex items-center justify-between gap-2">
                  <dt className="text-slate-400 font-normal">Hoàn tất lúc</dt>
                  <dd className="font-medium text-emerald-600 dark:text-emerald-400 font-mono text-right text-[11px]">
                    {formatDateTime(document.completed_at)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Cryptographic SHA-256 Hash Card */}
          <div className="card p-4 sm:p-5">
            <div className="flex items-center gap-2 text-sky-500 dark:text-sky-400 text-xs font-medium mb-3">
              <ShieldCheck className="w-4 h-4" />
              <span>Xác thực tính toàn vẹn (SHA-256)</span>
            </div>

            <div className="space-y-2.5">
              <div>
                <label className="text-[11px] text-slate-400 block mb-1 font-normal">
                  Mã băm tài liệu gốc
                </label>
                <div className="relative">
                  <div className="p-2.5 pr-8 rounded-lg bg-slate-50 dark:bg-slate-950/70 border border-slate-200 dark:border-white/10 font-mono text-[11px] text-slate-700 dark:text-sky-300 break-all leading-relaxed select-all">
                    {document.file_hash || 'SHA256_VERIFIED_IMMUTABLE_HASH'}
                  </div>
                  <button
                    onClick={copyFileHash}
                    className="absolute right-2 top-2 p-1 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
                    title="Sao chép mã hash"
                  >
                    {copiedHash ? (
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 font-normal leading-relaxed">
                Mã băm SHA-256 chứng minh nội dung tài liệu nguyên bản, không bị can thiệp sau khi ký theo Nghị định 30/2020/NĐ-CP.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Signer Modal with Contacts Multi-select & Quick Check */}
      {showAddSigner && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowAddSigner(false)}
        >
          <div
            className="bg-white dark:bg-[#111827] rounded-2xl shadow-2xl border border-slate-200 dark:border-white/10 max-w-xl w-full overflow-hidden animate-slide-up flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-sky-500" />
                  <span>Thêm người ký vào luồng</span>
                </h2>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  Chọn người từ danh bạ có sẵn hoặc nhập người ký mới
                </p>
              </div>
              <button
                onClick={() => setShowAddSigner(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Tabs */}
            <div className="flex border-b border-slate-100 dark:border-white/10 px-4 bg-slate-50/50 dark:bg-slate-900/50">
              <button
                type="button"
                onClick={() => setSignerModalTab('contacts')}
                className={`py-2.5 px-3 text-xs font-medium border-b-2 transition-all flex items-center gap-1.5 ${
                  signerModalTab === 'contacts'
                    ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                <Users className="w-3.5 h-3.5" />
                <span>Danh bạ người ký</span>
                <span className="ml-1 px-1.5 py-0.2 bg-sky-100 dark:bg-sky-950/60 text-sky-600 dark:text-sky-400 rounded-full text-[10px] font-semibold">
                  {contacts.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSignerModalTab('manual')}
                className={`py-2.5 px-3 text-xs font-medium border-b-2 transition-all flex items-center gap-1.5 ${
                  signerModalTab === 'manual'
                    ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`}
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Nhập người mới</span>
              </button>
              <Link
                to="/contacts"
                className="ml-auto py-2 px-2.5 text-xs text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 font-medium flex items-center gap-1.5 transition-colors"
                title="Mở trang Quản lý danh bạ để thêm, sửa, xóa cán bộ"
              >
                <Users className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Quản lý danh bạ</span>
                <ExternalLink className="w-3 h-3 opacity-70" />
              </Link>
            </div>

            {/* Tab 1: Contacts Multi-select List */}
            {signerModalTab === 'contacts' && (
              <div className="p-4 flex-1 overflow-y-auto space-y-3">
                {/* Search & Select All Bar */}
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Tìm theo họ tên, email, chức vụ..."
                      className="input pl-8 h-8 text-xs font-normal"
                    />
                    {contactSearch && (
                      <button
                        onClick={() => setContactSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>

                  {/* Select All Button */}
                  <button
                    type="button"
                    onClick={() => {
                      const eligible = contacts
                        .filter(c => !signers.some(s => s.email.toLowerCase() === c.email.toLowerCase()))
                        .map(c => c.id);
                      if (selectedContactIds.length === eligible.length) {
                        setSelectedContactIds([]);
                      } else {
                        setSelectedContactIds(eligible);
                      }
                    }}
                    className="btn-outline h-8 px-2.5 text-xs whitespace-nowrap flex items-center gap-1 font-normal"
                  >
                    <CheckSquare className="w-3.5 h-3.5 text-sky-500" />
                    <span>
                      {selectedContactIds.length > 0 && selectedContactIds.length === contacts.filter(c => !signers.some(s => s.email.toLowerCase() === c.email.toLowerCase())).length
                        ? 'Bỏ chọn hết'
                        : 'Chọn tất cả'}
                    </span>
                  </button>
                </div>

                {/* Contacts List */}
                <div className="space-y-1.5 max-h-[340px] overflow-y-auto pr-1">
                  {contacts
                    .filter(c => {
                      if (!contactSearch) return true;
                      const q = contactSearch.toLowerCase();
                      return (
                        c.full_name.toLowerCase().includes(q) ||
                        c.email.toLowerCase().includes(q) ||
                        (c.title && c.title.toLowerCase().includes(q))
                      );
                    })
                    .map((contact) => {
                      const isAlreadySigner = signers.some(
                        s => s.email.toLowerCase() === contact.email.toLowerCase()
                      );
                      const isSelected = selectedContactIds.includes(contact.id);
                      const currentRole = contactRoles[contact.id] || contact.role || 'signer';

                      const initials = contact.full_name
                        .split(' ')
                        .filter(Boolean)
                        .slice(-2)
                        .map(w => w[0]?.toUpperCase())
                        .join('') || 'NK';

                      return (
                        <div
                          key={contact.id}
                          onClick={() => {
                            if (!isAlreadySigner) {
                              setSelectedContactIds(prev =>
                                prev.includes(contact.id)
                                  ? prev.filter(id => id !== contact.id)
                                  : [...prev, contact.id]
                              );
                            }
                          }}
                          className={`p-3 rounded-xl border transition-all flex items-center gap-3 select-none ${
                            isAlreadySigner
                              ? 'bg-slate-50 dark:bg-slate-900/40 border-slate-200/60 dark:border-white/5 opacity-60 cursor-not-allowed'
                              : isSelected
                              ? 'border-sky-500 bg-sky-50/70 dark:bg-sky-500/10 cursor-pointer shadow-sm'
                              : 'border-slate-200 dark:border-white/10 hover:border-slate-300 dark:hover:border-white/20 hover:bg-slate-50/50 dark:hover:bg-slate-900/30 cursor-pointer'
                          }`}
                        >
                          {/* Checkbox */}
                          <div className="flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={isAlreadySigner}
                              onChange={() => {}} // Handled by parent div onClick
                              className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 border-slate-300 dark:border-slate-600 dark:bg-slate-800 pointer-events-none"
                            />
                          </div>

                          {/* Avatar Initials */}
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                            isSelected
                              ? 'bg-sky-500 text-white'
                              : 'bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                          }`}>
                            {initials}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-slate-900 dark:text-white truncate">
                                {contact.full_name}
                              </span>
                              {isAlreadySigner && (
                                <span className="text-[10px] bg-slate-200 dark:bg-slate-800 text-slate-500 px-1.5 py-0.2 rounded font-normal">
                                  Đã trong luồng
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 truncate font-normal">
                              <span>{contact.email}</span>
                              {contact.title && (
                                <>
                                  <span>•</span>
                                  <span className="text-slate-400 dark:text-slate-500">{contact.title}</span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Role selector for contact */}
                          {!isAlreadySigner && (
                            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                              <select
                                value={currentRole}
                                onChange={(e) => {
                                  const newRole = e.target.value as SignerRole;
                                  setContactRoles(prev => ({ ...prev, [contact.id]: newRole }));
                                  if (!isSelected) {
                                    setSelectedContactIds(prev => [...prev, contact.id]);
                                  }
                                }}
                                className="input h-7 text-[11px] font-normal py-0 px-2 bg-white dark:bg-slate-800 border-slate-200 dark:border-white/10"
                                title="Vai trò người ký"
                              >
                                <option value="signer">Ký chính</option>
                                <option value="approver">Duyệt</option>
                                <option value="cc">Bản sao (CC)</option>
                                <option value="witness">Làm chứng</option>
                              </select>

                              {/* Delete contact from list */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteContact(contact.id);
                                  setSelectedContactIds(prev => prev.filter(id => id !== contact.id));
                                }}
                                className="p-1 text-slate-400 hover:text-rose-500 transition-colors"
                                title="Xóa khỏi danh bạ liên hệ"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}

                  {contacts.length === 0 && (
                    <div className="text-center py-8 text-slate-400 text-xs">
                      <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                      <p>Chưa có người ký nào trong danh bạ.</p>
                      <button
                        type="button"
                        onClick={() => setSignerModalTab('manual')}
                        className="btn-outline h-7 text-xs mt-2"
                      >
                        Nhập người ký mới
                      </button>
                    </div>
                  )}
                </div>

                {/* Footer for Contacts Tab */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100 dark:border-white/10 gap-2">
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Đã chọn: <strong className="text-sky-500 font-semibold">{selectedContactIds.length}</strong> người
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddSigner(false);
                        setSelectedContactIds([]);
                      }}
                      className="btn-outline text-xs h-8 px-3 font-normal flex items-center gap-1.5"
                      disabled={actionLoading === 'send'}
                    >
                      <X className="w-3.5 h-3.5" />
                      <span>Hủy</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleAddSelectedSigners}
                      disabled={actionLoading === 'send' || selectedContactIds.length === 0}
                      className="btn-primary text-xs h-8 px-3.5 font-medium flex items-center gap-1.5"
                    >
                      {actionLoading === 'send' ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          <span>Đang thêm...</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-3.5 h-3.5" />
                          <span>Thêm</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: Manual Add Form */}
            {signerModalTab === 'manual' && (
              <form onSubmit={handleSubmit(handleSend)} className="p-4 space-y-3 flex-1 overflow-y-auto">
                <div>
                  <label htmlFor="email" className="label text-[11px] font-normal">
                    Email người nhận <span className="text-rose-500">*</span>
                  </label>
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    className={`input h-8 text-xs font-normal ${errors.email ? 'input-error' : ''}`}
                    placeholder="ho.ten@coquan.gov.vn"
                    disabled={actionLoading === 'send'}
                  />
                  {errors.email && (
                    <p className="mt-1 text-[11px] text-rose-500">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="full_name" className="label text-[11px] font-normal">
                    Họ và tên người ký <span className="text-rose-500">*</span>
                  </label>
                  <input
                    {...register('full_name')}
                    id="full_name"
                    type="text"
                    className={`input h-8 text-xs font-normal ${errors.full_name ? 'input-error' : ''}`}
                    placeholder="Nguyễn Văn A"
                    disabled={actionLoading === 'send'}
                  />
                  {errors.full_name && (
                    <p className="mt-1 text-[11px] text-rose-500">{errors.full_name.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="role" className="label text-[11px] font-normal">
                      Vai trò trong luồng
                    </label>
                    <select
                      {...register('role')}
                      id="role"
                      className="input h-8 text-xs font-normal py-0"
                      disabled={actionLoading === 'send'}
                    >
                      <option value="signer">Người ký chính</option>
                      <option value="approver">Người duyệt trước khi ký</option>
                      <option value="cc">Nhận bản sao lưu (CC)</option>
                      <option value="witness">Người làm chứng</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="signature_type" className="label text-[11px] font-normal">
                      Hình thức ký cho phép
                    </label>
                    <select
                      {...register('signature_type')}
                      id="signature_type"
                      className="input h-8 text-xs font-normal py-0"
                      disabled={actionLoading === 'send'}
                    >
                      <option value="draw">Vẽ chữ ký tay</option>
                      <option value="image">Tải ảnh con dấu / chữ ký</option>
                      <option value="text">Ký theo tên định dạng</option>
                      <option value="digital">Chứng thư số (PKI / Token)</option>
                    </select>
                  </div>
                </div>

                {document.workflow_type === 'sequential' && (
                  <div>
                    <label htmlFor="order" className="label text-[11px] font-normal">
                      Thứ tự ký
                    </label>
                    <input
                      {...register('order', { valueAsNumber: true })}
                      id="order"
                      type="number"
                      min="1"
                      className="input h-8 text-xs font-normal font-mono"
                      placeholder="1 = Ký đầu tiên"
                      disabled={actionLoading === 'send'}
                    />
                  </div>
                )}

                <div className="pt-1">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={saveToContacts}
                      onChange={(e) => setSaveToContacts(e.target.checked)}
                      className="w-4 h-4 rounded text-sky-600 focus:ring-sky-500 border-slate-300 dark:border-slate-600 dark:bg-slate-800"
                    />
                    <span className="text-xs text-slate-600 dark:text-slate-300">
                      Lưu người này vào danh bạ để lần sau chỉ cần tick chọn
                    </span>
                  </label>
                </div>

                <div className="flex gap-2 pt-3 border-t border-slate-100 dark:border-white/10">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSigner(false);
                      reset();
                    }}
                    className="btn-outline flex-1 text-xs h-8 font-normal flex items-center justify-center gap-1.5"
                    disabled={actionLoading === 'send'}
                  >
                    <X className="w-3.5 h-3.5" />
                    <span>Hủy</span>
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1 text-xs h-8 font-medium flex items-center justify-center gap-1.5"
                    disabled={actionLoading === 'send'}
                  >
                    {actionLoading === 'send' ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Đang thêm...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>Thêm</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}